import SlimSelect from "slim-select";

// CRO-120: drives the inline "buy online" form rendered in the colour-detail
// hero (Views/Partials/Hero/ColourDetailHero.cshtml). Selecting a paint type
// repopulates the product dropdown, which repopulates the finish dropdown and
// points the buy button at the correct B&Q / TradePoint product page.
const DEFAULT_PRODUCT_NAME = "Premium Walls & Ceilings";

// Ready Mix: a visitor who reaches a colour from one of the Ready Mix pages came
// looking for Ready Mix, so that product is pulled to the top of the paint
// dropdown and preselected instead of the usual default.
//
// Two ways in. The Ready Mix listing block stamps this marker onto the links it
// renders (Views/Partials/blocklist/Components/ReadyMixColours.cshtml), which is
// explicit and survives being shared or bookmarked.
const READY_MIX_PARAM = "paint";
const READY_MIX_PARAM_VALUE = "ready-mix";

// The referrer then covers every other link on those pages (rich text, promos,
// carousels) without each block having to opt in. Same-origin navigations still
// carry the full path under the site's strict-origin-when-cross-origin policy.
const READY_MIX_SOURCE_PATHS = [
    "/ready-mix-paint/",
    "/paint-products/walls-ceilings/ready-mix/",
];

function hasReadyMixMarker() {
    try {
        return (
            new URLSearchParams(window.location.search).get(READY_MIX_PARAM) ===
            READY_MIX_PARAM_VALUE
        );
    } catch (e) {
        return false;
    }
}

function cameFromReadyMixPage() {
    if (!document.referrer) return false;
    try {
        const referrer = new URL(document.referrer);
        if (referrer.origin !== window.location.origin) return false;
        const path = referrer.pathname.toLowerCase().replace(/\/+$/, "") + "/";
        return READY_MIX_SOURCE_PATHS.includes(path);
    } catch (e) {
        return false;
    }
}

export default function ColourDetailBuy() {
    document.querySelectorAll("[data-buy-form]").forEach(setupBuyForm);
}

function setupBuyForm(form) {
    const dataEl = form.querySelector("[data-buy-data]");
    const productSelect = form.querySelector("[data-buy-product]");
    const finishSelect = form.querySelector("[data-buy-finish]");
    const buyBtn = form.querySelector("[data-buy-button]");

    if (!dataEl || !productSelect || !finishSelect || !buyBtn) return;

    let categories;
    try {
        categories = JSON.parse(dataEl.textContent);
    } catch (e) {
        return;
    }
    if (!Array.isArray(categories) || !categories.length) return;

    // Same prefix match the view uses to spot Ready Mix products, so the distinct
    // nodes ("Ready Mix", "Ready Mix Tester Pot", ...) are all recognised.
    const readyMixName = (form.dataset.buyReadymix || "").trim().toLowerCase();
    const isReadyMix = (product) =>
        readyMixName !== "" &&
        (product.name || "").trim().toLowerCase().startsWith(readyMixName);

    const wantsReadyMixFirst =
        readyMixName !== "" && (hasReadyMixMarker() || cameFromReadyMixPage());

    // Reorder up front rather than at render time, so the dropdown order, the
    // default selection and the finish/buy-button sync all read the same list.
    // Sort is stable, so everything else keeps its authored order.
    if (wantsReadyMixFirst) {
        categories.forEach((category) => {
            category.products.sort((a, b) => Number(isReadyMix(b)) - Number(isReadyMix(a)));
        });
    }

    // SlimSelect copies the <select>'s own classes onto both the control
    // (.ss-main) and the body-appended dropdown (.ss-content), so the
    // colour-detail-buy__select class is the shared styling hook for both.
    const productSS = new SlimSelect({ select: productSelect, settings: { showSearch: false } });
    const finishSS = new SlimSelect({ select: finishSelect, settings: { showSearch: false } });

    const thumb = (image, size) =>
        `${image}?width=${size}&height=${size}&rmode=boxpad`;

    const buildProductData = (products) =>
        products.map((p) => ({
            text: p.name,
            value: p.name,
            html: p.image
                ? `<img class="colour-detail-buy__product-img" width="32" height="32" src="${thumb(p.image, 32)}" srcset="${thumb(p.image, 64)} 2x" alt="" loading="lazy"><span class="colour-detail-buy__product-name">${p.name}</span>`
                : `<span class="colour-detail-buy__product-name">${p.name}</span>`,
        }));

    const buildFinishData = (product) =>
        product.bq.map((b) => ({ text: b.type, value: b.type }));

    const getCheckedCategory = () => {
        const checked = form.querySelector("[data-buy-cat]:checked");
        return checked ? categories[parseInt(checked.value, 10)] : categories[0];
    };

    const getCurrentProduct = (category) =>
        category.products.find((p) => p.name === productSelect.value) || category.products[0];

    const updateBuyButton = (product, finishType) => {
        const entry = product.bq.find((b) => b.type === finishType) || product.bq[0];
        if (!entry) return;
        // Ready Mix entries carry a complete B&Q URL (no shared base / colour-ref segment),
        // so use it verbatim; standard products keep the assembled base/colour-ref/EAN path.
        buyBtn.href = entry.fullUrl
            ? entry.fullUrl
            : `${entry.link}/${product.colourRef}/${entry.productUrl}`;

        const retail = buyBtn.querySelector(".retail-link");
        const trade = buyBtn.querySelector(".trade-link");
        if (retail) retail.style.display = product.isTrade ? "none" : "";
        if (trade) trade.style.display = product.isTrade ? "" : "none";
    };

    const syncFinishAndBuy = (product) => {
        finishSS.setData(buildFinishData(product));
        const finishType = product.bq[0] ? product.bq[0].type : "";
        updateBuyButton(product, finishType);
    };

    const syncCategory = (category, preferredName) => {
        if (!category || !category.products.length) return;
        productSS.setData(buildProductData(category.products));

        let product = category.products[0];
        if (preferredName) {
            const found = category.products.find((p) => p.name === preferredName);
            if (found) {
                product = found;
                productSS.setSelected(preferredName);
            }
        }
        syncFinishAndBuy(product);
    };

    // Initial render: coming from a Ready Mix page, open on the paint type that
    // stocks Ready Mix (now first in that list, so no preferred name is needed).
    // Otherwise default to the standard product where it exists.
    const readyMixCategoryIndex = wantsReadyMixFirst
        ? categories.findIndex((category) => category.products.some(isReadyMix))
        : -1;

    if (readyMixCategoryIndex > -1) {
        const readyMixRadio = form.querySelector(
            `[data-buy-cat][value="${readyMixCategoryIndex}"]`
        );
        if (readyMixRadio) readyMixRadio.checked = true;
        syncCategory(categories[readyMixCategoryIndex]);
    } else {
        syncCategory(getCheckedCategory(), DEFAULT_PRODUCT_NAME);
    }

    form.querySelectorAll("[data-buy-cat]").forEach((radio) => {
        radio.addEventListener("change", () => {
            const checked = form.querySelector("[data-buy-cat]:checked");
            if (!checked) return;
            syncCategory(categories[parseInt(checked.value, 10)]);
        });
    });

    productSelect.addEventListener("change", () => {
        const product = getCurrentProduct(getCheckedCategory());
        if (product) syncFinishAndBuy(product);
    });

    finishSelect.addEventListener("change", () => {
        const product = getCurrentProduct(getCheckedCategory());
        if (product) updateBuyButton(product, finishSelect.value);
    });
}
